/**
 * Pure timezone math shared by scheduling code paths.
 *
 * Scheduling must compute local planning times in the USER's timezone, not
 * the server's. These helpers convert between UTC epochs and
 * zoned wall-clock parts using only Intl (no extra dependencies), matching the
 * approach already used by weeklyCalendarPlanner.
 */

export type ZonedParts = {
  year: number;
  month: number; // 1-12
  day: number;
  hour: number;
  minute: number;
  second: number;
};

// Servers resolve the user's zone through resolveCalendarTimezone
// (src/features/calendar/core/userClock): saved preference, then the client's
// reported zone, then UTC, never the host zone. Clients use clientUserTimezone.

export function isValidTimezone(timezone: string): boolean {
  try {
    Intl.DateTimeFormat("en-US", { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

const partsFormatterCache = new Map<string, Intl.DateTimeFormat>();

function getPartsFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = partsFormatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: timezone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    partsFormatterCache.set(timezone, formatter);
  }
  return formatter;
}

/** Wall-clock parts of a UTC epoch as seen in the given timezone. */
export function getZonedParts(timezone: string, utcMs: number): ZonedParts {
  const byType = new Map<string, string>();
  for (const part of getPartsFormatter(timezone).formatToParts(new Date(utcMs))) {
    byType.set(part.type, part.value);
  }
  return {
    year: Number(byType.get("year")),
    month: Number(byType.get("month")),
    day: Number(byType.get("day")),
    // Intl reports midnight as "24" with hour12:false in some engines.
    hour: Number(byType.get("hour")) % 24,
    minute: Number(byType.get("minute")),
    second: Number(byType.get("second")),
  };
}

/**
 * UTC epoch for a wall-clock time in the given timezone.
 * Double-corrects the offset so DST transitions resolve to a valid instant.
 */
export function zonedTimeToUtcMs(
  timezone: string,
  parts: { year: number; month: number; day: number; hour?: number; minute?: number; second?: number; millisecond?: number },
): number {
  const { year, month, day, hour = 0, minute = 0, second = 0, millisecond = 0 } = parts;
  const initialGuess = Date.UTC(year, month - 1, day, hour, minute, second, millisecond);
  const initialOffset = getZonedOffsetMinutes(timezone, initialGuess);
  const adjusted = initialGuess - initialOffset * 60_000;
  const adjustedOffset = getZonedOffsetMinutes(timezone, adjusted);
  const result = initialGuess - adjustedOffset * 60_000;
  if (initialOffset === adjustedOffset) return result;
  // Near an offset change. A wall time skipped by spring-forward (02:30 in
  // Los Angeles on DST day) has no instant; read it on the pre-change offset,
  // which lands after the gap (03:30 PDT), not an hour before it.
  const zoned = getZonedParts(timezone, result);
  if (zoned.year === year && zoned.month === month && zoned.day === day && zoned.hour === hour && zoned.minute === minute) return result;
  return initialGuess - Math.min(initialOffset, adjustedOffset) * 60_000;
}

/** Minutes the zone is ahead of UTC at the given instant. */
export function getZonedOffsetMinutes(timezone: string, utcMs: number): number {
  const zoned = getZonedParts(timezone, utcMs);
  const asUtc = Date.UTC(zoned.year, zoned.month - 1, zoned.day, zoned.hour, zoned.minute, zoned.second);
  // Truncate the epoch's sub-minute part the same way the parts were.
  const truncated = utcMs - (utcMs % 1000);
  return Math.round((asUtc - truncated) / 60_000);
}

/** UTC epoch of the start of the zoned calendar day containing `utcMs`, shifted by `dayOffset` days. */
export function getZonedDayStartUtcMs(timezone: string, utcMs: number, dayOffset = 0): number {
  const parts = getZonedParts(timezone, utcMs);
  // Search for the first instant in the requested local date. Converting
  // midnight as a wall time is ambiguous when an offset change skips midnight
  // entirely (for example Santiago), and can resolve into the previous date.
  const targetDate = Date.UTC(parts.year, parts.month - 1, parts.day + dayOffset);
  let before = targetDate - 48 * 60 * 60_000;
  let after = targetDate + 48 * 60 * 60_000;
  while (after - before > 1) {
    const middle = Math.floor((before + after) / 2);
    const local = getZonedParts(timezone, middle);
    const date = Date.UTC(local.year, local.month - 1, local.day);
    if (date < targetDate) before = middle;
    else after = middle;
  }
  // If a whole date was skipped, this is the first instant of the next
  // available date, preserving calendar-day rather than 24-hour arithmetic.
  return after;
}

/** UTC epoch of hour:minute wall-clock on the zoned day containing `dayAnchorUtcMs`. */
export function setZonedTimeOfDay(timezone: string, dayAnchorUtcMs: number, hour: number, minute = 0): number {
  const parts = getZonedParts(timezone, dayAnchorUtcMs);
  return zonedTimeToUtcMs(timezone, {
    year: parts.year,
    month: parts.month,
    day: parts.day,
    hour,
    minute,
  });
}

/** Header the browser uses to report its IANA zone on the auth request. */
export const CLIENT_TIMEZONE_HEADER = "x-client-timezone";

/** A trimmed IANA zone `Intl` accepts, or null for anything else. */
export function normalizeTimeZone(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const zone = value.trim();
  if (!zone || zone.length > 64 || !isValidTimezone(zone)) return null;
  return zone;
}

export function detectBrowserTimeZone(): string | null {
  try {
    return normalizeTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
  } catch {
    return null;
  }
}

/**
 * CLIENT ONLY. The zone to show the signed-in user's times in: their saved
 * zone, else this device's zone (the best evidence a client has before the
 * zone is captured), else UTC. Never call this on a server: the host zone is
 * UTC on Vercel and Trigger and the developer's own zone in local dev, so a
 * server must resolve through resolveCalendarTimezone instead.
 */
export function clientUserTimezone(preferences: { timezone?: unknown } | null | undefined): string {
  return normalizeTimeZone(preferences?.timezone) ?? detectBrowserTimeZone() ?? "UTC";
}

const DAY_MS = 86_400_000;

/**
 * Places a date-only due that was stored at UTC midnight on its local day. A
 * date without a time ("2026-09-25", or Google Tasks' "2026-09-25T00:00:00.000Z")
 * parsed as UTC lands at 00:00Z, which west of UTC is the evening before. This
 * returns the local end of that UTC calendar date in `timezone`, as the
 * date-only writers store it. Any other epoch, or an invalid zone, comes back
 * unchanged. Call it only for a due known to be date-only: 00:00Z is also
 * 5pm PDT, 8pm EDT and other real deadlines.
 */
const localEndOfDayCache = new Map<string, number>();

export function utcMidnightDueToLocalEndOfDay(epoch: number, timezone: string | null | undefined): number {
  if (!Number.isFinite(epoch) || epoch % DAY_MS !== 0 || !timezone) return epoch;
  // Planner sorts call this per comparison, so memoize (bounded).
  const cacheKey = `${timezone}|${epoch}`;
  const cached = localEndOfDayCache.get(cacheKey);
  if (cached !== undefined) return cached;
  try {
    getPartsFormatter(timezone);
  } catch {
    return epoch;
  }
  const date = new Date(epoch);
  const result = zonedTimeToUtcMs(timezone, {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth() + 1,
    day: date.getUTCDate(),
    hour: 23,
    minute: 59,
    second: 59,
    millisecond: 999,
  });
  if (localEndOfDayCache.size >= 4096) localEndOfDayCache.clear();
  localEndOfDayCache.set(cacheKey, result);
  return result;
}
